"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, Suspense } from "react";
import { signIn } from "next-auth/react";
import { useSearchParams } from "next/navigation";
import { motion, AnimatePresence } from "framer-motion";
import { CrucibleMarkAnimated } from "@/components/brand/crucible-mark";
import { CrucibleLockup } from "@/components/brand/crucible-lockup";
import { Mail, Lock, LogIn, UserPlus, AlertCircle, Clock, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { InlineAlert, InlineAlertDescription } from "@/components/ui/inline-alert";
import { AccessRequestModal } from "./access-request-modal";
import { useAppVersionLabel } from "@/components/providers/app-release-provider";

// Compare to the string: `!!process.env.X` is true for the literal "false",
// so NEXT_PUBLIC_SSO_ENABLED=false previously still enabled SSO -- and now
// also governs whether the password lane starts collapsed.
const SSO_ENABLED = process.env.NEXT_PUBLIC_SSO_ENABLED === "true";

// Loading fallback
function LoginSkeleton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex h-screen items-center justify-center bg-cr-substrate"><Loader2 className="size-8 animate-spin text-cr-brand" /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}

// Login content component
function LoginContent() {
  const versionLabel = useAppVersionLabel();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showAccessRequest, setShowAccessRequest] = useState(false);
  // Staff sign in with Microsoft. The password lane is for accounts without an
  // Entra identity (dev/test, demo), so it starts collapsed whenever SSO is
  // available -- people were defaulting to it and using their Entra password,
  // which the backend now rejects for SSO accounts.
  const [showPasswordLane, setShowPasswordLane] = useState(!SSO_ENABLED);
  const searchParams = useSearchParams();
  const callbackUrl = searchParams.get("callbackUrl") || "/mainchat";
  const authError = searchParams.get("error");
  // IdleGuard signs people out to /login?r=idle (the short param is WAF-safe;
  // see idle-guard.tsx). Say why, since they are not signed in to be toasted.
  const signedOutForInactivity = searchParams.get("r") === "idle";
  const [error, setError] = useState<string | null>(() => {
    if (!authError) return null;
    return authError === "CredentialsSignin" ? "Invalid credentials. 3PH and HRCRS staff should use Sign in with Microsoft." : "An error occurred during sign in.";
  });
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    const domain = email.split("@")[1]?.toLowerCase();
    if (!["3phalliance.com", "hrcrs.com"].includes(domain)) {
      setError("Only @3phalliance.com and @hrcrs.com emails are allowed");
      setIsLoading(false);
      return;
    }
    try {
      const result = await signIn("credentials", {
        email: email.toLowerCase().trim(),
        password,
        redirect: false,
        callbackUrl
      });
      if (result?.error) {
        setError(result.error === "CredentialsSignin" ? "Invalid credentials. 3PH and HRCRS staff should use Sign in with Microsoft." : "An error occurred during sign in.");
      } else if (result?.ok) {
        window.location.href = callbackUrl;
      }
    } catch {
      setError("An unexpected error occurred");
    } finally {
      setIsLoading(false);
    }
  };
  return <div className="dark relative flex h-dvh overflow-hidden bg-cr-substrate text-foreground" style={{
    colorScheme: "dark"
  }}>
      {/* Left Panel - Hero */}
      <div className="relative hidden min-w-0 flex-1 flex-col items-center justify-center overflow-hidden bg-cr-substrate lg:flex">
        {/* Mark and wordmark are ONE centred stack. They used to be a centred
            mark plus a bottom-pinned text block, two positioning systems in one
            box, which collided by 33px at 1024x800 once the decorative rings
            stopped filling the gap between them. */}
        <div className="flex w-full max-w-[600px] flex-col items-center gap-4 px-6 text-center">
          <CrucibleMarkAnimated className="mb-2 h-[180px]" />
          <span className="text-[11px] tracking-[3px] text-cr-brand/[0.53]">
            [ AI-NATIVE STUDY DESIGN ]
          </span>
          <h1 className="text-[60px] font-extrabold tracking-[16px] -me-[16px] text-foreground">
            CRUCIBLE
          </h1>
          <p className="text-lg tracking-[0.5px] text-cr-text-3">
            Forge the path to evidence.
          </p>
          <p className="text-xs tracking-[1.2px] text-cr-text-4">
            Protocol Design · Regulatory Intelligence · Data Standards
            <br />· Trial Operations · RWE
          </p>
        </div>

        {/* Version Badge */}
        <div className="absolute bottom-10 left-auto right-10">
          <span className="text-[11px] tracking-[1.5px] text-cr-text-4">
            {versionLabel}
          </span>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex min-h-0 w-full shrink-0 flex-col overflow-y-auto bg-cr-substrate px-5 lg:w-[520px] lg:border-l lg:border-cr-border-2 lg:px-12">
        <div className="mx-auto flex w-full max-w-[420px] flex-1 flex-col items-center justify-center gap-8 py-16 lg:py-20">
          {/* Form Card */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="w-full rounded-[20px] border border-cr-border-2 bg-cr-surface p-7 shadow-2xl lg:p-10">
            {/* Mobile brand block — hidden on md+ because the hero panel shows branding */}
            <CrucibleLockup variant="auth-mobile" stacked className="mb-5 flex lg:hidden" />

            {/* Header */}
            <div className="mb-7 flex flex-col items-center gap-2">
              <h2 className="text-[30px] font-bold tracking-tight text-foreground">
                Welcome back
              </h2>
              <p className="text-sm text-cr-text-4">
                Sign in to your workspace
              </p>
            </div>

            {signedOutForInactivity && <InlineAlert variant="info" className="mb-5" data-testid="login-idle-notice">
                <Clock aria-hidden />
                <InlineAlertDescription>
                  You were signed out after a period of inactivity.
                </InlineAlertDescription>
              </InlineAlert>}

            {/* Error Message */}
            <AnimatePresence mode="wait">
              {error && <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: "auto"
            }} exit={{
              opacity: 0,
              height: 0
            }} className="mb-5 flex items-start gap-2 rounded-lg border border-red-500/20 bg-red-500/10 p-3">
                  <AlertCircle className="size-4 shrink-0 text-red-500" />
                  <p className="text-sm text-red-500">{error}</p>
                </motion.div>}
            </AnimatePresence>

            {/* Microsoft SSO Button (shown when Entra is configured) */}
            {SSO_ENABLED && <div className="mb-6 space-y-3">
                <Button type="button" onClick={() => signIn("microsoft-entra-id", {
              callbackUrl
            })} className="flex h-12 w-full items-center gap-3 rounded-xl border border-cr-border-2 bg-cr-substrate text-cr-text-2 hover:bg-cr-surface hover:text-foreground">
                  <svg className="size-4 shrink-0" viewBox="0 0 21 21" fill="none">
                    <rect x="1" y="1" width="9" height="9" fill="#F25022" />
                    <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
                    <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
                    <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
                  </svg>
                  Sign in with Microsoft
                </Button>
                {!showPasswordLane && <button type="button" onClick={() => setShowPasswordLane(true)} className="w-full text-center text-xs text-cr-text-4 underline-offset-4 hover:text-cr-text-2 hover:underline">
                    Developer sign-in
                  </button>}
                {showPasswordLane && <div className="flex items-center gap-4">
                    <div className="h-px flex-1 bg-cr-elevated" />
                    <span className="text-xs uppercase tracking-wider text-cr-text-4">
                      or
                    </span>
                    <div className="h-px flex-1 bg-cr-elevated" />
                  </div>}
              </div>}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {showPasswordLane && <>
                  {/* Email Field */}
                  <div className="space-y-2">
                    <label htmlFor="email" className="text-sm font-medium text-cr-text-2">
                      Email
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-cr-text-4" />
                      <Input id="email" type="email" value={email} onChange={e_0 => setEmail(e_0.target.value)} placeholder="name@3phalliance.com" required className="h-12 border-cr-border-2 bg-cr-substrate pl-10 text-foreground placeholder:text-cr-text-4" />
                    </div>
                  </div>

                  {/* Password Field */}
                  <div className="space-y-2">
                    <label htmlFor="password" className="text-sm font-medium text-cr-text-2">
                      Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-cr-text-4" />
                      <Input id="password" type="password" value={password} onChange={e_1 => setPassword(e_1.target.value)} placeholder="Enter your password" required className="h-12 border-cr-border-2 bg-cr-substrate pl-10 text-foreground placeholder:text-cr-text-4" />
                    </div>
                  </div>

                  {/* Sign In Button */}
                  <Button type="submit" disabled={isLoading} className="h-12 w-full rounded-xl bg-gradient-to-b from-cr-brand to-cr-brand-hover font-semibold text-white shadow-lg transition-transform hover:scale-[1.02]" style={{
                boxShadow: "0 4px 20px rgba(255, 92, 0, 0.25)"
              }}>
                    {isLoading ? <Loader2 className="size-4 animate-spin" /> : <LogIn className="size-4" />}
                    {isLoading ? "Signing in..." : "Sign In"}
                  </Button>

                  {/* Divider */}
                  <div className="flex items-center gap-4 py-1">
                    <div className="h-px flex-1 bg-cr-elevated" />
                    <span className="text-xs uppercase tracking-wider text-cr-text-4">
                      or
                    </span>
                    <div className="h-px flex-1 bg-cr-elevated" />
                  </div>
                </>}

              {/* Request Access Button */}
              <Button type="button" variant="outline" onClick={() => setShowAccessRequest(true)} className="h-12 w-full rounded-xl border-cr-border-2 bg-cr-substrate text-cr-text-2 hover:bg-cr-substrate hover:text-foreground">
                <UserPlus className="size-4" />
                Request Access
              </Button>
            </form>

            {/* Footer Note */}
            <p className="mt-7 text-center text-[11px] text-cr-text-3">
              Only @3phalliance.com and @hrcrs.com emails
            </p>
          </motion.div>

          {/* Built-by line. No certification badges: only claims the team can stand behind. */}
          <div className="flex w-full shrink-0 flex-col items-center gap-3">
            <div className="h-px w-[200px] bg-cr-elevated" />
            <p className="text-[11px] tracking-wider text-cr-text-3">
              Built by 3PH Alliance
            </p>
          </div>
        </div>
      </div>

      {/* Access Request Modal */}
      <AccessRequestModal isOpen={showAccessRequest} onClose={() => setShowAccessRequest(false)} />
    </div>;
}

// Main page component with Suspense boundary
export default function LoginPage() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Suspense fallback={<LoginSkeleton />}><LoginContent /></Suspense>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}